// =============================================================================
// CALL-TO-ACTION SECTION COMPONENT (V2 - NORMALIZED)
// =============================================================================

import React from 'react';
import { Button } from '@/components/Button';
import { cn } from '@/utils';
import { createActionHandler, convertButtonToAction } from '@/utils/actionHandler';
import { layoutToClasses, displayToStyles } from '@/types/layout';
import type { ContentComponent } from '@/types';
import type { CTAComponent } from '@/types/components';

interface CTASectionProps {
  component: ContentComponent;
}

/**
 * Type guard for new CTAComponent format
 */
function isNewCTAFormat(component: ContentComponent): boolean {
  return (
    component.type === 'cta' &&
    'content' in component &&
    typeof (component as any).content === 'object' &&
    'title' in (component as any).content
  );
}

/**
 * CTA Section Component
 */
export const CTASection: React.FC<CTASectionProps> = ({ component }) => {
  if (isNewCTAFormat(component)) {
    return <CTASectionNew component={component as unknown as any} />;
  }
  return <CTASectionLegacy component={component} />;
};

/**
 * New format CTA Section
 */
const CTASectionNew: React.FC<{ component: CTAComponent }> = ({ component }) => {
  const { id, content, media, actions, layout, display } = component;
  
  const layoutClasses = layoutToClasses(layout);
  const displayStyles = displayToStyles(display);
  
  const hasBackground = media?.image || display.background?.image;
  
  return (
    <section
      id={id}
      className={cn(
        'content-section cta-section',
        layoutClasses,
        hasBackground && 'cta-section--with-bg'
      )}
      style={displayStyles}
    >
      {hasBackground && display.background?.overlay && (
        <div
          className="cta-overlay"
          style={{
            backgroundColor: display.background.overlay.color || 'rgba(0, 0, 0, 0.4)',
            opacity: display.background.overlay.opacity || 0.4,
          }}
        />
      )}
      
      <div className="container">
        <div
          className={cn(
            'cta-content',
            `cta-content--${layout.alignment || 'center'}`
          )}
        >
          {content.title && (
            <h2 className="cta-title heading-style">{content.title}</h2>
          )}
          {content.body && (
            <p className="cta-description body-style">{content.body}</p>
          )}
          
          {actions.length > 0 && (
            <div className="cta-actions">
              {actions.map((action, index) => (
                <Button
                  key={action.id || index}
                  variant={action.variant || 'primary'}
                  size={action.size || 'lg'}
                  className={cn(
                    'cta-button',
                    `cta-button--${action.variant || 'primary'}`
                  )}
                  onClick={createActionHandler(action)}
                  disabled={action.disabled}
                >
                  {action.label}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

/**
 * Legacy format CTA Section
 */
const CTASectionLegacy: React.FC<{ component: ContentComponent }> = ({ component }) => {
  const { id, title, content, metadata } = component;
  
  const buttons = metadata?.buttons || [];
  const backgroundImage = metadata?.backgroundImage;
  const textAlign = metadata?.textAlign || 'center';
  const overlayOpacity = metadata?.overlayOpacity || 0.4;
  const minHeight = metadata?.minHeight || '50vh';
  
  // Convert old button format to actions
  const actions = buttons.map(convertButtonToAction);
  
  const sectionStyle = backgroundImage ? {
    backgroundImage: `url(${backgroundImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    position: 'relative' as const,
    minHeight,
  } : {
    minHeight,
  };
  
  return (
    <section
      id={id}
      className={cn(
        'content-section cta-section',
        backgroundImage && 'cta-section--with-bg'
      )}
      style={sectionStyle}
    >
      {backgroundImage && (
        <div 
          className="cta-overlay"
          style={{ opacity: overlayOpacity }}
        />
      )}
      
      <div className="container">
        <div
          className={cn(
            'cta-content',
            `cta-content--${textAlign}`
          )}
        >
          {title && (
            <h2 className="cta-title heading-style">{title}</h2>
          )}
          {content && (
            <p className="cta-description body-style">{content}</p>
          )}
          
          {actions.length > 0 && (
            <div className="cta-actions">
              {actions.map((action, index) => (
                <Button
                  key={index}
                  variant={action.variant || 'primary'}
                  size={action.size || 'lg'}
                  className={cn(
                    'cta-button',
                    `cta-button--${action.variant || 'primary'}`
                  )}
                  onClick={createActionHandler(action)}
                >
                  {action.label}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export { CTASectionNew, CTASectionLegacy };

